import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  AppView,
  AuthService,
  buildListingSlugSegments,
  FavoritesService,
  HostListingsService,
  INotification,
  IPublicUser,
  MessageService,
  NotificationService,
  SearchApiService,
  ToastService,
} from '@cnt-workspace/data-access';
import { ILatLon, Role } from '@curbnturf/entities';
import { Subject, Subscription, combineLatest, combineLatestWith, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { CinematicRollDirective } from '../directives/cinematic-roll.directive';

/** Row shape for the navbar search dropdown — a merged view over
 *  IPoiSearchResult / IListingSearchResult, trimmed to what the UI needs. */
interface NavAutocompleteResult {
  id?: number;
  name: string;
  type?: string;
  city?: string;
  state?: string;
  price?: number;
  lat?: number;
  lon?: number;
}

@Component({
  selector: 'cnt-navbar',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive, CinematicRollDirective],
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit, OnDestroy, AfterViewInit {
  private platformId = inject<object>(PLATFORM_ID);
  private router = inject(Router);
  private auth = inject(AuthService);
  private toasts = inject(ToastService);
  private msg = inject(MessageService);
  private notifySvc = inject(NotificationService);
  private favoritesSvc = inject(FavoritesService);
  private hostListings = inject(HostListingsService);
  private searchSvc = inject(SearchApiService);

  private readonly MIN_QUERY_LENGTH = 3;

  /** /search below 992px: the navbar carries a compact "Where to?" search pill
   *  in place of the hidden desktop search, and stays put on scroll so the
   *  sticky filter row under it never jumps. */
  @Input() searchMode = false;
  /** Placeholder for the search pill, e.g. the current destination on /search. */
  @Input() searchPlaceholder: string | null = null;

  /** "Use my location" row, offered when the search input is focused and empty. */
  nearMeOpen = false;

  isNavbarVisible = true;
  mobileNavOpen = false;
  userMenuOpen = false;
  scrolled = false;
  isHome = false;
  searchQuery = '';
  autocompleteResults: NavAutocompleteResult[] = [];
  autocompleteOpen = false;
  autocompleteLoading = false;
  activeIndex = -1;
  private coordinates?: ILatLon;
  favoritesCount = 0;
  unreadMessages = 0;
  user: IPublicUser | null = null;
  view: AppView = 'guest';
  /** True when the signed-in user owns at least one listing — drives
   *  the navbar Hosting-calendar shortcut on the user dropdown. */
  hasOwned = false;
  /** True for sales/admin roles — drives the Sales » Clients nav link. */
  hasGrowAccess = false;

  /** Notifications dropdown. */
  notificationsOpen = false;
  notifications: INotification[] = [];
  unreadNotifications = 0;

  private lastScrollY = 0;
  private unreadSub: Subscription | null = null;
  private notifySub: Subscription | null = null;
  private favoritesSub: Subscription | null = null;
  private hasOwnedSub: Subscription | null = null;
  private searchSub: Subscription | null = null;
  private readonly searchTerms = new Subject<string>();
  private readonly TRANSPARENT_THRESHOLD = 80;
  /** Downward scroll (px) needed to hide the navbar — ignores toolbar-resize jitter. */
  private readonly HIDE_DELTA_PX = 6;
  /** Within this distance (px) of the page bottom the navbar is always shown. */
  private readonly BOTTOM_REVEAL_PX = 24;

  @ViewChild('navbarRoot') navbarRoot?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchWrapper') searchWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('userMenuWrapper') userMenuWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('notificationsWrapper') notificationsWrapper?: ElementRef<HTMLDivElement>;

  ngOnInit(): void {
    this.isHome = this.router.url === '/' || this.router.url.startsWith('/?');
    this.favoritesSub = this.favoritesSvc.favorites$
      .pipe(map((list) => list.length))
      .subscribe((count) => (this.favoritesCount = count));
    this.auth.currentUser$.subscribe((u) => {
      this.user = u;
      this.hasGrowAccess = u?.role === Role.sales || u?.role === Role.salesManager || u?.role === Role.admin;
      this.unreadSub?.unsubscribe();
      this.unreadSub = null;
      this.unreadMessages = 0;
      if (u) {
        this.unreadSub = this.msg.unreadFor$(u.email).subscribe((n) => (this.unreadMessages = n));
      }
    });
    this.hasOwnedSub = combineLatest([this.auth.currentUser$, this.hostListings.myListings$])
      .pipe(map(([u, listings]) => !!u && listings.length > 0))
      .subscribe((has) => (this.hasOwned = has));
    this.auth.currentView$.subscribe((v) => (this.view = v));
    this.notifySub = this.notifySvc.notifications$.subscribe((list) => {
      this.notifications = list;
      this.unreadNotifications = list.filter((n) => !n.read).length;
    });
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      this.isHome = e.urlAfterRedirects === '/' || e.urlAfterRedirects.startsWith('/?');
      this.mobileNavOpen = false;
      this.userMenuOpen = false;
      this.notificationsOpen = false;
      this.autocompleteOpen = false;
    });

    if (isPlatformBrowser(this.platformId) && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => (this.coordinates = { lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => undefined,
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
      );
    }

    this.searchSub = this.searchTerms
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.length < this.MIN_QUERY_LENGTH) {
            this.autocompleteLoading = false;
            return of([] as NavAutocompleteResult[]);
          }

          this.autocompleteLoading = true;
          const query = {
            queryTerm: term,
            locationLat: this.coordinates?.lat,
            locationLon: this.coordinates?.lon,
          };

          return this.searchSvc.searchForPoiAutocomplete(query).pipe(
            combineLatestWith(this.searchSvc.searchForListingAutocomplete(query)),
            map(([poiResult, listingResult]) => [
              ...listingResult.results.slice(0, 5).map(
                (r): NavAutocompleteResult => ({
                  id: r.id,
                  name: r.name,
                  type: r.type,
                  city: r.city,
                  state: r.state,
                  price: r.price,
                }),
              ),
              ...poiResult.results.slice(0, 5).map(
                (r): NavAutocompleteResult => ({
                  name: r.name,
                  state: r.state,
                  lat: r.location.lat,
                  lon: r.location.lon,
                }),
              ),
            ]),
            catchError(() => of([] as NavAutocompleteResult[])),
          );
        }),
      )
      .subscribe((results) => {
        this.autocompleteLoading = false;
        this.autocompleteResults = results;
        this.autocompleteOpen = results.length > 0;
        this.activeIndex = -1;
      });
  }

  ngOnDestroy(): void {
    this.unreadSub?.unsubscribe();
    this.notifySub?.unsubscribe();
    this.favoritesSub?.unsubscribe();
    this.hasOwnedSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
    this.notificationsOpen = false;
  }
  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  toggleNotifications(): void {
    this.notificationsOpen = !this.notificationsOpen;
    this.userMenuOpen = false;
  }
  closeNotifications(): void {
    this.notificationsOpen = false;
  }

  onNotificationClick(n: INotification): void {
    this.notifySvc.markRead(n.id);
    this.notificationsOpen = false;
    this.router.navigateByUrl(n.routerLink);
  }

  markAllNotificationsRead(): void {
    this.notifySvc.markAllRead(this.notifications);
  }

  /** Tailwind text-* color class for a notification icon based on its tone. */
  notifyIconClass(n: INotification): string {
    switch (n.tone) {
      case 'trinidad':
        return 'text-trinidad bg-trinidad/10';
      case 'jungle':
        return 'text-jungle-green bg-jungle-green/10';
      case 'gold':
        return 'bg-gold/20';
      default:
        return 'text-muted-text bg-cream/60';
    }
  }

  notifyIconStyle(n: INotification): Record<string, string> | null {
    // Inline color for the gold tone — the brand gold doesn't have a built-in text utility.
    return n.tone === 'gold' ? { color: '#b3760e' } : null;
  }

  notifyTimeLabel(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    if (ms < 7 * 86_400_000) return `${Math.floor(ms / 86_400_000)}d ago`;
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }

  async signOut(): Promise<void> {
    await this.auth.signOut();
    this.userMenuOpen = false;
    this.toasts.info('Signed out.');
    this.router.navigate(['/']);
  }

  switchView(): void {
    const next: AppView = this.view === 'host' ? 'guest' : 'host';
    this.auth.setView(next);
    this.userMenuOpen = false;
    this.router.navigate([next === 'host' ? '/hosting' : '/user-dashboard/dashboard']);
  }

  get signInQueryParams(): Record<string, string> {
    return { returnTo: this.router.url };
  }

  get userInitials(): string {
    if (!this.user) return '';
    const f = this.user.firstName?.[0] || '';
    const l = this.user.lastName?.[0] || '';
    return (f + l).toUpperCase();
  }

  get verifiedSinceLabel(): string {
    if (!this.user?.verifiedAt) return '';
    try {
      const d = new Date(this.user.verifiedAt);
      return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
    } catch {
      return '';
    }
  }

  /** True when the navbar should render its translucent over-hero state. */
  get transparent(): boolean {
    return this.isHome && !this.scrolled;
  }

  toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
  }

  closeMobileNav(): void {
    this.mobileNavOpen = false;
  }

  onSearchSubmit(): void {
    const q = this.searchQuery.trim();
    const queryParams = q ? { dest: q } : {};
    this.router.navigate(['/search'], { queryParams });
    this.searchQuery = '';
    this.autocompleteOpen = false;
    this.searchInput?.nativeElement.blur();
  }

  onSearchFocus(): void {
    if (!this.searchQuery.trim()) this.nearMeOpen = true;
  }

  /** Search stays near the visitor: centers /search on them and sorts by distance. */
  useMyLocation(): void {
    this.nearMeOpen = false;
    this.autocompleteOpen = false;
    this.searchInput?.nativeElement.blur();
    const go = (c: ILatLon) =>
      this.router.navigate(['/search'], {
        queryParams: { lat: c.lat, lon: c.lon, zoom: 10, sort: 'nearest', near: 1 },
      });
    if (this.coordinates) {
      go(this.coordinates);
      return;
    }
    if (!isPlatformBrowser(this.platformId) || !navigator.geolocation) {
      this.toasts.error('Location isn\'t available on this device.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.coordinates = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        go(this.coordinates);
      },
      () => this.toasts.error('We couldn\'t get your location. Check location permissions and try again.'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  }

  onSearchQueryChange(value: string): void {
    this.searchQuery = value;
    this.nearMeOpen = !value.trim();
    this.activeIndex = -1;
    this.searchTerms.next(value.trim());
  }

  isBoondock(result: NavAutocompleteResult): boolean {
    return result.type === 'boondock' || (result.type === 'standard' && !result.price);
  }

  selectAutocompleteResult(result: NavAutocompleteResult): void {
    this.autocompleteOpen = false;
    this.searchQuery = '';
    this.searchInput?.nativeElement.blur();

    if (result.id) {
      this.router.navigate(
        buildListingSlugSegments(this.isBoondock(result) ? 'boondocking' : 'rv-camping', {
          id: result.id,
          title: result.name,
          city: result.city,
          state: result.state,
        }),
      );
      return;
    }

    this.router.navigate(['/search'], {
      queryParams: { dest: result.name, lat: result.lat, lon: result.lon, zoom: 12 },
    });
  }

  onSearchKeydown(e: KeyboardEvent): void {
    if (!this.autocompleteOpen || this.autocompleteResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % this.autocompleteResults.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + this.autocompleteResults.length) % this.autocompleteResults.length;
    } else if (e.key === 'Enter' && this.activeIndex >= 0) {
      e.preventDefault();
      this.selectAutocompleteResult(this.autocompleteResults[this.activeIndex]);
    } else if (e.key === 'Escape') {
      this.autocompleteOpen = false;
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Clamp to the real scroll range: mobile overscroll bounce reports values
    // past either end, and settling back from them reads as a direction change.
    const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const rawScrollY = window.scrollY || document.documentElement.scrollTop;
    const currentScrollY = Math.min(Math.max(rawScrollY, 0), maxScrollY);
    const delta = currentScrollY - this.lastScrollY;
    this.scrolled = currentScrollY > this.TRANSPARENT_THRESHOLD;
    // Keep the navbar in place while the mobile menu is open, or scrolling the
    // page behind it would slide the open menu off-screen. At the bottom there's
    // no further to scroll, and on iOS the first swipe back up only restores the
    // browser toolbars (no scroll event), so reveal it there too.
    if (this.searchMode || this.mobileNavOpen || currentScrollY <= 100 || currentScrollY >= maxScrollY - this.BOTTOM_REVEAL_PX) {
      this.isNavbarVisible = true;
    } else if (delta >= this.HIDE_DELTA_PX) {
      this.isNavbarVisible = false;
    } else if (delta < 0) {
      this.isNavbarVisible = true;
    }
    this.lastScrollY = currentScrollY;
    this.publishNavOffset();
  }

  ngAfterViewInit(): void {
    this.publishNavOffset();
  }

  /** Publishes where the navbar's bottom edge is (0 while it's slid away) as
   *  --cnt-nav-offset, so sticky page headers (the listing wizard) can sit
   *  right under it instead of at a fixed guess. */
  private publishNavOffset(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = this.navbarRoot?.nativeElement;
    if (!el) return;
    // The visible bar is .block-navbar; the fixed wrapper around it has
    // transparent padding below, which would leave a see-through gap.
    const bar = el.querySelector<HTMLElement>('.block-navbar') ?? el;
    const bottom = this.isNavbarVisible ? el.offsetTop + bar.offsetTop + bar.offsetHeight : 0;
    document.documentElement.style.setProperty('--cnt-nav-offset', `${bottom}px`);
  }

  /** Close any open popovers when clicking outside their wrappers. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.userMenuOpen) {
      const w = this.userMenuWrapper?.nativeElement;
      if (w && !w.contains(event.target as Node)) this.userMenuOpen = false;
    }
    if (this.notificationsOpen) {
      const w = this.notificationsWrapper?.nativeElement;
      if (w && !w.contains(event.target as Node)) this.notificationsOpen = false;
    }
    if (this.autocompleteOpen || this.nearMeOpen) {
      const w = this.searchWrapper?.nativeElement;
      if (w && !w.contains(event.target as Node)) {
        this.autocompleteOpen = false;
        this.nearMeOpen = false;
      }
    }
  }

  /** Close on Escape too. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.userMenuOpen) this.userMenuOpen = false;
    if (this.notificationsOpen) this.notificationsOpen = false;
    if (this.autocompleteOpen) this.autocompleteOpen = false;
    if (this.nearMeOpen) this.nearMeOpen = false;
    if (this.mobileNavOpen) this.mobileNavOpen = false;
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKey(e: KeyboardEvent): void {
    if (e.key !== '/') return;
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const tag = t.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || t.isContentEditable) return;
    e.preventDefault();
    this.searchInput?.nativeElement.focus();
  }
}
